import { Fragment, type ComponentProps, type RefObject } from "react"
import type { Row } from "@tanstack/react-table"
import { useVirtualizer } from "@tanstack/react-virtual"
import cn from "@/v2-ui/utils/utils.cn"
import { useTableContext } from "./table.context"
import TableBodyRowGroup from "./TableBodyRowGroup"
import TableBodyRow from "./TableBodyRow"
import TableSkeletonRows from "./TableSkeletonRows"

/** Below this many rows the window costs more than it saves. */
const VIRTUALIZE_THRESHOLD = 60
/** Board rows are ~40px; measureElement corrects per row after paint. */
const ESTIMATED_ROW_HEIGHT = 40
/** Enough placeholder rows to read as "more coming", few enough not to scroll past. */
const FETCH_MORE_SKELETON_ROW_COUNT = 4

export type TableBodyProps<TData = unknown> = ComponentProps<"tbody"> & {
  rows: Row<TData>[]
  /** The scrolling container (Table.Content's element) the window tracks. */
  scrollRef?: RefObject<HTMLDivElement | null>
  /**
   * The next page is on its way: placeholder rows stand in for it below
   * the last loaded row, where the operator is looking, until it lands.
   */
  isFetchingMore?: boolean
}

/**
 * The table body, windowed for large row sets: only the rows in (and
 * around) the viewport render, so a board that has accumulated thousands
 * of fetched rows keeps a flat DOM and a quiet main thread. Grouped and
 * row-expanded states render in full — grouping changes the row model's
 * shape, and an open detail row means the operator is reading, not
 * scrolling — as do small tables, where a window would be overhead.
 */
function TableBody<TData = unknown>(props: TableBodyProps<TData>) {
  const {
    rows,
    scrollRef,
    isFetchingMore = false,
    className,
    ...tbodyProps
  } = props
  const {
    table,
    callbacksRef,
    getHighlightedRowId,
    expandedRowId,
    renderRowDetail,
  } = useTableContext<TData>()

  function handleRowClick(row: Row<TData>) {
    callbacksRef.current.onRowClick?.(row)
  }

  const hasRowClick = !!callbacksRef.current.onRowClick

  const isGrouped = table.getState().grouping.length > 0
  const hasExpandedRow = !!renderRowDetail && expandedRowId != null
  const virtualized =
    !!scrollRef &&
    !isGrouped &&
    !hasExpandedRow &&
    rows.length > VIRTUALIZE_THRESHOLD

  const virtualizer = useVirtualizer({
    count: virtualized ? rows.length : 0,
    getScrollElement: () => scrollRef?.current ?? null,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 12,
  })

  if (rows.length === 0) return null

  function renderRow(
    row: Row<TData>,
    measureRef?: (el: HTMLTableRowElement | null) => void,
    index?: number,
  ) {
    if (row.getIsGrouped()) {
      return <TableBodyRowGroup key={row.id} row={row} />
    }

    const isExpanded = !!renderRowDetail && row.id === expandedRowId

    return (
      <Fragment key={row.id}>
        <TableBodyRow<TData>
          row={row}
          data-index={index}
          measureRef={measureRef}
          isSelected={row.getIsSelected()}
          isActive={(getHighlightedRowId?.(row) ?? false) || isExpanded}
          isClickable={hasRowClick}
          onRowClick={handleRowClick}
        />
        {isExpanded ? (
          <tr className="ui-table-body-detail-row">
            <td colSpan={table.getVisibleLeafColumns().length}>
              {renderRowDetail(row)}
            </td>
          </tr>
        ) : null}
      </Fragment>
    )
  }

  const skeletonRows = isFetchingMore ? (
    <TableSkeletonRows rowCount={FETCH_MORE_SKELETON_ROW_COUNT} />
  ) : null

  if (!virtualized) {
    return (
      <tbody className={cn("ui-table-body", className)} {...tbodyProps}>
        {rows.map((row) => renderRow(row))}
        {skeletonRows}
      </tbody>
    )
  }

  const virtualItems = virtualizer.getVirtualItems()
  const paddingTop = virtualItems[0]?.start ?? 0
  const paddingBottom =
    virtualizer.getTotalSize() - (virtualItems.at(-1)?.end ?? 0)
  const columnCount = table.getVisibleLeafColumns().length

  return (
    <tbody className={cn("ui-table-body", className)} {...tbodyProps}>
      {paddingTop > 0 && (
        <tr aria-hidden style={{ height: paddingTop }}>
          <td colSpan={columnCount} />
        </tr>
      )}
      {virtualItems.map((virtualItem) =>
        renderRow(
          rows[virtualItem.index],
          virtualizer.measureElement,
          virtualItem.index,
        ),
      )}
      {paddingBottom > 0 && (
        <tr aria-hidden style={{ height: paddingBottom }}>
          <td colSpan={columnCount} />
        </tr>
      )}
      {skeletonRows}
    </tbody>
  )
}

export default TableBody
